import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Sun, Anchor } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { destinations } from "../data/vessels";
import { DetailModal } from "./DetailModal";

export const Destinations = ({ onPlan }) => {
  const { t } = useLanguage();
  const [selected, setSelected] = useState(null);

  return (
    <section id="destinations" className="relative py-24 sm:py-36 bg-[#0B1528]" data-testid="destinations-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap items-end justify-between gap-8 mb-16 sm:mb-20"
        >
          <div className="max-w-xl">
            <p className="font-label text-[10px] sm:text-xs tracking-[0.45em] uppercase text-[#D4AF37] mb-5" data-testid="destinations-overline">
              {t.destinations.overline}
            </p>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl text-[#F7F5F0] leading-[1.05]" data-testid="destinations-title">
              {t.destinations.title}
            </h2>
          </div>
          <p className="text-sm text-slate-400 font-light max-w-xs leading-relaxed">{t.destinations.sub}</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6">
          {destinations.map((d, i) => {
            const item = t.destinations.items[d.id];
            return (
              <motion.div
                key={d.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.9, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => setSelected(d)}
                className={`group relative overflow-hidden cursor-pointer border border-[rgba(212,175,55,0.12)] hover:border-[rgba(212,175,55,0.4)] transition-colors duration-700 ${
                  d.featured ? "sm:col-span-2 lg:col-span-6 aspect-[16/7]" : "lg:col-span-3 aspect-[16/10]"
                }`}
                data-testid={`destination-card-${d.id}`}
              >
                <img
                  src={d.image}
                  alt={item.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050B14]/95 via-[#050B14]/25 to-transparent" />
                <span className="absolute top-5 right-5 flex items-center gap-2 font-label text-[9px] tracking-[0.25em] uppercase bg-[#050B14]/70 backdrop-blur-md border border-[rgba(212,175,55,0.35)] text-[#F3E5AB] px-4 py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  {t.details.common.detailsHint}
                  <ArrowUpRight size={12} />
                </span>
                <div className="absolute inset-x-0 bottom-0 p-7 sm:p-9">
                  <p className="font-label text-[9px] tracking-[0.4em] uppercase text-[#D4AF37] mb-2">{item.tag}</p>
                  <h3 className="font-display text-3xl sm:text-4xl text-[#F7F5F0] font-light">{item.name}</h3>
                  <p className="mt-2.5 text-sm text-slate-300/90 font-light max-w-md leading-relaxed opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <DetailModal
        open={!!selected}
        onClose={() => setSelected(null)}
        image={selected?.image}
        imageAlt={selected ? t.destinations.items[selected.id].name : ""}
        tag={selected ? t.destinations.items[selected.id].tag : ""}
        title={selected ? t.destinations.items[selected.id].name : ""}
        testid="destination-modal"
      >
        {selected && (
          <>
            <p className="text-sm sm:text-base text-slate-300 font-light leading-relaxed" data-testid="destination-modal-text">
              {t.details.destinations[selected.id].long1}
            </p>
            <p className="mt-4 text-sm sm:text-base text-slate-400 font-light leading-relaxed">
              {t.details.destinations[selected.id].long2}
            </p>

            <div className="mt-10">
              <p className="font-label text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-5">{t.details.common.highlights}</p>
              <div className="flex flex-wrap gap-3" data-testid="destination-modal-highlights">
                {t.details.destinations[selected.id].highlights.map((h) => (
                  <span key={h} className="font-label text-[10px] tracking-[0.12em] uppercase border border-[rgba(212,175,55,0.3)] text-[#F3E5AB] px-4 py-2">
                    {h}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[rgba(212,175,55,0.18)] border border-[rgba(212,175,55,0.18)]">
              <div className="bg-[#0B1528] p-5 flex items-start gap-4">
                <Sun size={18} className="text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="font-label text-[9px] tracking-[0.3em] uppercase text-slate-500 mb-1.5">{t.details.common.season}</p>
                  <p className="font-display text-xl text-[#F7F5F0]" data-testid="destination-modal-season">{t.details.destinations[selected.id].season}</p>
                </div>
              </div>
              <div className="bg-[#0B1528] p-5 flex items-start gap-4">
                <Anchor size={18} className="text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="font-label text-[9px] tracking-[0.3em] uppercase text-slate-500 mb-1.5">{t.details.common.ports}</p>
                  <p className="text-sm text-[#F7F5F0] font-light" data-testid="destination-modal-ports">{t.details.destinations[selected.id].ports}</p>
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-[rgba(212,175,55,0.18)]">
              <button
                onClick={() => {
                  const name = t.destinations.items[selected.id].name;
                  setSelected(null);
                  setTimeout(() => onPlan(name), 120);
                }}
                data-testid="destination-modal-plan-button"
                className="font-label text-[11px] tracking-[0.25em] uppercase px-8 py-4 bg-[#D4AF37] text-[#050B14] font-semibold hover:bg-[#F3E5AB] transition-colors duration-300"
              >
                {t.details.common.planCta}
              </button>
            </div>
          </>
        )}
      </DetailModal>
    </section>
  );
};
